/**
 * FaxConsole — the CRM "Fax" tab (/crm/fax): one hub for everything fax.
 *
 * Sub-tabs (Phone-console pattern, persisted via getPref/setPref('fax.tab') and
 * deep-linkable via ?tab=): Unmatched (the triage queue), All faxes (searchable
 * history), Send (standalone one-off send), and Provider offices (the directory).
 * The legacy /crm/records and /crm/provider-offices routes redirect here with
 * ?tab=unmatched / ?tab=offices.
 *
 * The live sending fax number (read-only, from GET /api/crm/fax/config) shows
 * small in the top-right as a reference for staff.
 */
import { useCallback, useEffect, useState } from 'react';
import { useSearchParams } from 'react-router-dom';
import { Building2, History, Inbox, Printer, Send } from 'lucide-react';
import type { ReactNode } from 'react';
import api from '../../services/api';
import { FaxConfig } from '../types';
import { getPref, setPref, STATS_CHANGED_EVENT } from '../lib';
import { PageHeader, SegmentedTabs } from '../components';
import UnmatchedFaxes from '../components/fax/UnmatchedFaxes';
import AllFaxes from '../components/fax/AllFaxes';
import SendFax from '../components/fax/SendFax';
import ProviderOfficesPanel from '../components/fax/ProviderOfficesPanel';

type Tab = 'unmatched' | 'all' | 'send' | 'offices';
const TABS: { key: Tab; label: string; icon: ReactNode }[] = [
  { key: 'unmatched', label: 'Unmatched', icon: <Inbox className="h-4 w-4" /> },
  { key: 'all', label: 'All faxes', icon: <History className="h-4 w-4" /> },
  { key: 'send', label: 'Send', icon: <Send className="h-4 w-4" /> },
  { key: 'offices', label: 'Provider offices', icon: <Building2 className="h-4 w-4" /> },
];
const TAB_KEYS = TABS.map((t) => t.key);
const isTab = (v: string | null): v is Tab => !!v && (TAB_KEYS as string[]).includes(v);

export default function FaxConsole() {
  const [searchParams, setSearchParams] = useSearchParams();
  const [tab, setTab] = useState<Tab>(() => {
    const fromUrl = searchParams.get('tab');
    if (isTab(fromUrl)) return fromUrl;
    const fromPref = getPref('fax.tab', 'unmatched');
    return isTab(fromPref) ? fromPref : 'unmatched';
  });

  const onTab = useCallback((t: Tab) => {
    setTab(t);
    setPref('fax.tab', t);
    // Keep the URL in sync so a refresh / bookmark lands on the same sub-tab.
    setSearchParams((prev) => { prev.set('tab', t); return prev; }, { replace: true });
  }, [setSearchParams]);

  // A redirect (e.g. /records -> ?tab=unmatched) can arrive after mount.
  useEffect(() => {
    const fromUrl = searchParams.get('tab');
    if (isTab(fromUrl) && fromUrl !== tab) setTab(fromUrl);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [searchParams]);

  // Live fax number (read-only reference) + the unmatched count for the tab badge.
  const [config, setConfig] = useState<FaxConfig | null>(null);
  const [unmatched, setUnmatched] = useState<number | null>(null);
  useEffect(() => {
    let alive = true;
    api.get('/api/crm/fax/config').then((res) => { if (alive) setConfig(res.data || null); }).catch(() => {});
    const loadCount = () => {
      api.get('/api/crm/contacts/stats')
        .then((res) => { if (alive) setUnmatched(typeof res.data?.fax_needs_match === 'number' ? res.data.fax_needs_match : null); })
        .catch(() => {});
    };
    loadCount();
    window.addEventListener(STATS_CHANGED_EVENT, loadCount);
    return () => { alive = false; window.removeEventListener(STATS_CHANGED_EVENT, loadCount); };
  }, []);

  return (
    <div className="space-y-4">
      <PageHeader
        icon={Printer}
        title="Fax"
        subtitle="Triage inbound faxes, search history, send one-off faxes, and manage provider offices."
        actions={
          config?.from_number_display && (
            <div className="text-right text-xs text-gray-400 dark:text-gray-500">
              <span className="block">Our fax line</span>
              <span className="font-medium text-gray-600 dark:text-gray-300">{config.from_number_display}</span>
            </div>
          )
        }
      />

      <SegmentedTabs
        label="Fax console sections"
        value={tab}
        onChange={(v) => onTab(v as Tab)}
        tabs={TABS.map(({ key, label, icon }) => ({
          value: key,
          label,
          icon,
          // Only the Unmatched tab carries a triage-queue badge.
          count: key === 'unmatched' && typeof unmatched === 'number' && unmatched > 0 ? unmatched : null,
        }))}
      />

      {tab === 'unmatched' && <UnmatchedFaxes />}
      {tab === 'all' && <AllFaxes />}
      {tab === 'send' && <SendFax onSent={() => onTab('all')} />}
      {tab === 'offices' && <ProviderOfficesPanel />}
    </div>
  );
}
